import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';
import type { RoutingMode } from '../../core/types/room.types';
import type {
  SourceAnalysisPayload,
  SourceAnalysisRuntime,
} from '../../core/types/payloads/source_analysis.types';
import { validateSourceAnalysisPayload } from '../../core/schemas/humanitiesBlockSchemas';

export function validateSourceAnalysisPayloadFromLogic(raw: unknown): SourceAnalysisPayload {
  return validateSourceAnalysisPayload(raw);
}

export function createInitialSourceAnalysisRuntime(
  _payload: SourceAnalysisPayload,
  _routing: RoutingMode,
): SourceAnalysisRuntime {
  return { responses: {}, finalized: {}, aggregates: { submitted: 0 } };
}

export const sourceAnalysisLogic: ActivityLogicPlugin<'source_analysis'> = {
  mode: 'source_analysis',
  validatePayload: validateSourceAnalysisPayloadFromLogic,
  createInitialRuntime: createInitialSourceAnalysisRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant) {
    const pid = participant.participantId;
    const responses = { ...(ctx.runtime.responses[pid] ?? {}) };

    if (input.action === 'DRAFT') {
      const questionIndex = Number((input.data as { questionIndex?: number })?.questionIndex);
      const text = String((input.data as { text?: string })?.text ?? '');
      if (!Number.isFinite(questionIndex) || questionIndex < 0) {
        return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_QUESTION' };
      }
      return {
        runtime: {
          ...ctx.runtime,
          responses: { ...ctx.runtime.responses, [pid]: { ...responses, [questionIndex]: text } },
        },
      };
    }

    if (input.action === 'SUBMIT') {
      const finalized = { ...ctx.runtime.finalized, [pid]: true };
      const submitted = Object.keys(finalized).filter((id) => finalized[id]).length;
      return {
        runtime: {
          ...ctx.runtime,
          finalized,
          aggregates: { submitted },
        },
      };
    }

    return { runtime: ctx.runtime, rejected: true, rejectReason: 'UNKNOWN_ACTION' };
  },

  onHostAction(ctx, _action: HostActionPayload) {
    return { runtime: ctx.runtime };
  },
};

export function demoSourceAnalysisPayload(): SourceAnalysisPayload {
  // Neutro — sin Portales/Chile 1822 (fuga Historia vía defaultPayloadForMode).
  return validateSourceAnalysisPayloadFromLogic({
    title: 'Análisis de fuente',
    source: {
      quote:
        'El testimonio describe el contexto del tema y los hechos que el curso debe contrastar con otras evidencias.',
      reference: 'Fuente de ejemplo (neutra)',
    },
    questions: [
      '¿Cuál es la idea central del fragmento?',
      '¿Qué evidencias concretas aporta el texto?',
      '¿Cómo se relaciona este testimonio con el tema de la clase?',
    ],
  });
}
